import React, { useEffect, useState } from 'react';
import { CFormLabel } from '@coreui/react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useAuth } from '../contexts/AuthContext';
import { useReport } from '../contexts/ReportContext';
import { useCompanySetting } from '../contexts/CompanySettingContext';
import { colors, formatDateToBackend, openFileWithLoading } from '../components/Helper'
import { reportNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import PieChart from '../components/PieChart';
import BarChart from '../components/BarChart';
import PageHeader from '../components/PageHeader';
import DateRangeSelection from '../components/DateRangeSelection';
import ViewSingleReceiptReport from '../components/ViewSingleReceiptReport';

function ProductReport() {
    const { productReport, productReportCharts, productReportPDF, productReportCSV, } = useReport();
    const { currency } = useCompanySetting()
    const { restaurantId } = useAuth();

    const currentDate = new Date();
    const yesterdayDate = new Date(currentDate);
    yesterdayDate.setDate(currentDate.getDate() - 1);

    const [dates, setDates] = useState([yesterdayDate, currentDate]);
    const [receiptId, setReceiptId] = useState(null);
    const [reportList, setReportList] = useState([])
    const [sortField, setSortField] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedProductRows, setExpandedProductRows] = useState([]);
    const [paymentChartData, setPaymentChartData] = useState([])
    const [sellingProducts, setSellingProducts] = useState({
        x: [],
        y: []
    })

    const startDate = (dates.length > 0 && dates[0]) ? formatDateToBackend(dates[0]) : null
    const endDate = (dates.length > 0 && dates[1]) ? formatDateToBackend(dates[1]) : null

    const fetchProductReport = async () => {
        try {
            setIsLoading(true);
            setReportList([]);
            setTotalRecords(0);

            const reports = await productReport(currentPage, rowsPerPage, sortField, sortOrder, startDate, endDate);
            if (reports.data.length > 0) {
                setReportList(reports.data);
                setTotalRecords(reports.totalCount);
            }

        } catch (error) {
            console.error('Error fetching product reports', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchProductReport();
        }
    }, [currentPage, rowsPerPage, sortField, sortOrder, restaurantId, startDate, endDate]);

    const fetchProductReportCharts = async () => {
        try {
            setIsLoading(true);

            const reports = await productReportCharts(startDate, endDate);
            if (reports.data.length > 0) {
                const paymentData = reports.data.map((item, index) => ({
                    id: item.id,
                    value: parseFloat(item.totalPrice),
                    label: item.productName,
                    color: colors[index % colors.length]
                }));

                setPaymentChartData(paymentData);

                const product = reports.data.map(item => item.productName);
                const totalQuantity = reports.data.map(item => parseFloat(item.totalQuantity));

                setSellingProducts({
                    x: product,
                    y: totalQuantity
                })
            } else {
                setPaymentChartData([]);
                setSellingProducts({
                    x: [],
                    y: []
                })
            }

            setIsLoading(false);
        } catch (error) {
            console.error('Error fetching product report charts', error);
        }
    };

    useEffect(() => {
        if (restaurantId && startDate && endDate) {
            fetchProductReportCharts();
        }
    }, [restaurantId, startDate, endDate]);

    const handleSorting = async (e) => {
        const field = e.sortField;
        const order = e.sortOrder;

        setSortField(field);
        setSortOrder(order);
        fetchProductReport()
    };

    const onPageChange = (event) => {
        const newCurrentPage = Math.floor(event.first / event.rows) + 1;
        setCurrentPage(newCurrentPage);
        const newRowsPerPage = event.rows;
        setRowsPerPage(newRowsPerPage);
    };

    const generatePDF = async (pdfType) => {
        openFileWithLoading({
            apiCall: productReportPDF,
            apiParams: [startDate, endDate, pdfType],
            fileType: 'pdf',
            loadingMessage: `Generating ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF...`,
            errorMessage: `Failed to generate ${pdfType === 'summary' ? 'Summary' : 'Detailed'} PDF`,
        });
    }

    const generateCSV = async (csvType) => {
        openFileWithLoading({
            apiCall: productReportCSV,
            apiParams: [startDate, endDate, csvType],
            fileType: 'csv',
            loadingMessage: `Generating ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV...`,
            errorMessage: `Failed to generate ${csvType === 'summary' ? 'Summary' : 'Detailed'} CSV`,
        });
    }

    const rowExpansionTemplate = (rowData) => {
        const items = rowData.productLogDetails;

        return (
            <div>
                {items && items.length > 0 && (
                    <DataTable
                        className='product-report-expansion-table report-table'
                        lazy
                        value={items}
                        dataKey="id"
                    >
                        <Column
                            field="quantity"
                            header="Ordered Amount"
                            className='receipt_report_table quantity'
                        />
                        <Column
                            field="currentStock"
                            className='receipt_report_table currentStock p-0'
                            body={(rowData) => (
                                <div>
                                    {0}
                                </div>
                            )}
                        />
                        <Column
                            field="product_id"
                            className='receipt_report_table product_id p-0'
                        />
                        <Column
                            field="name"
                            className='receipt_report_table report_product_name p-0'
                        />
                        <Column
                            field="price"
                            className='receipt_report_table price'
                            body={(rowData) => (
                                <div>
                                    {rowData.price ? `${currency}${rowData.price}` : '0.00'}
                                </div>
                            )}
                        />
                        <Column
                            field="tax_rate"
                            className='receipt_report_table tax_rate'
                            body={(rowData) => (
                                <div>
                                    {'0.00'}
                                </div>
                            )} />
                        <Column
                            field="modified_price"
                            className='receipt_report_table tax_rate'
                            body={(rowData) => (
                                <div>
                                    {rowData.modified_price ? `${currency}${rowData.modified_price}` : '0.00'}
                                </div>
                            )}
                        />
                        <Column
                            field="totalCost"
                            className='receipt_report_table totalCost'
                            body={(rowData) => (
                                <div>
                                    {`${currency}0.00`}
                                </div>
                            )}
                        />
                        <Column
                            field="modified_price"
                            className='receipt_report_table  profit'
                            body={(rowData) => (
                                <div>
                                    {rowData.modified_price ? `${currency}${rowData.modified_price}` : '0.00'}
                                </div>
                            )}
                        />
                    </DataTable>
                )}
            </div>
        );
    };

    return (
        <Layout2 items={reportNav.items} navTitle={reportNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Product Reports'} />
                <div className='d-flex flex-row px-4 py-2 user-list-nav justify-content-between'>
                    <p className='pt-2'>
                        Print or download the product reports: <span className='view_button' onClick={() => generatePDF('summary')}>PDF report</span> / <span className='view_button' onClick={() => generateCSV('summary')}>CSV</span>
                    </p>
                    <DateRangeSelection
                        dates={dates}
                        setDates={setDates}
                    />
                </div>
                <div className='px-4 py-2'>
                    {
                        receiptId && (
                            <ViewSingleReceiptReport
                                receiptId={receiptId}
                                setReceiptId={setReceiptId}
                            />
                        )
                    }
                    <div className='report-chart-section d-flex justify-content-between mt-3'>
                        {
                            sellingProducts.x.length > 0 && sellingProducts.y.length > 0 && (
                                <div className='w-100 text-center me-5'>
                                    <CFormLabel>Top Selling Products</CFormLabel>
                                    <BarChart
                                        x={sellingProducts.x}
                                        y={sellingProducts.y}
                                        yLabel={'Count'}
                                    />
                                </div>
                            )
                        }
                        {
                            paymentChartData && paymentChartData.length > 0 && (
                                <div className='w-100 text-center self-center'>
                                    <CFormLabel>Product Revenue</CFormLabel>
                                    <PieChart
                                        data={paymentChartData}
                                    />
                                </div>
                            )
                        }
                    </div>
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <DataTable
                            className="customerTable thead-dark report-table"
                            totalRecords={totalRecords}
                            lazy
                            paginator
                            sortField={sortField}
                            sortOrder={sortOrder}
                            onSort={(e) => {
                                if (!e.sortField && e.sortOrder === 0) {
                                    handleSorting({ sortField: 'createdAt', sortOrder: -1 });
                                } else {
                                    handleSorting(e);
                                }
                            }}
                            removableSort
                            rows={rowsPerPage}
                            value={reportList}
                            first={(currentPage - 1) * rowsPerPage}
                            onPage={onPageChange}
                            dataKey="id"
                            emptyMessage="No product reports found."
                            expandedRows={expandedProductRows}
                            onRowToggle={(e) => setExpandedProductRows(e.data)}
                            rowExpansionTemplate={rowExpansionTemplate}
                        >
                            <Column
                                field="totalQuantity"
                                header="Ordered Amount"
                                className='receipt_report_table quantity'
                                sortable
                            />
                            <Column
                                field="currentStock"
                                header="Current Stock"
                                className='receipt_report_table currentStock'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {0}
                                    </div>
                                )}
                            />
                            <Column
                                field="productId"
                                header="Product Id"
                                className='receipt_report_table product_id'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {(rowData.productLogDetails && rowData.productLogDetails.length > 0) ? (
                                            <span
                                                className='view_button'
                                                onClick={() => {
                                                    const expandedRows = expandedProductRows ? [...expandedProductRows] : [];
                                                    const rowIndex = expandedRows.indexOf(rowData);

                                                    if (rowIndex >= 0) {
                                                        expandedRows.splice(rowIndex, 1);
                                                    } else {
                                                        expandedRows.push(rowData);
                                                    }

                                                    setExpandedProductRows(expandedRows);
                                                }}
                                            >
                                                {rowData.productId}
                                            </span>
                                        ) : (
                                            <span>
                                                {rowData.productId}
                                            </span>
                                        )
                                        }
                                    </div>
                                )}
                            />
                            <Column
                                field="name"
                                header="Product"
                                className='receipt_report_table report_product_name'
                                sortable
                            />
                            <Column
                                field="price"
                                header="Price"
                                className='receipt_report_table price'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.price ? `${currency}${rowData.price}` : '0.00'}
                                    </div>
                                )}
                            />
                            <Column
                                field="tax_rate"
                                header="VAT"
                                className='receipt_report_table tax_rate'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.tax_rate ? `${rowData.tax_rate}%` : '0.00%'}
                                    </div>
                                )}
                            />
                            <Column
                                field="totalPrice"
                                header="Total Revenue"
                                className='receipt_report_table tax_rate'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.price ? `${currency}${rowData.totalPrice}` : '0.00'}
                                    </div>
                                )}
                            />
                            <Column
                                field="totalCost"
                                header="Cost"
                                className='receipt_report_table totalCost'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.totalCost ? `${currency}${rowData.totalCost}` : '0.00'}
                                    </div>
                                )}
                            />
                            <Column
                                field="totalProfit"
                                header="Profit"
                                className='receipt_report_table profit'
                                sortable
                                body={(rowData) => (
                                    <div>
                                        {rowData.totalProfit ? `${currency}${rowData.totalProfit}` : '0.00'}
                                    </div>
                                )}
                            />
                        </DataTable>
                    )}
                </div>
            </div>
        </Layout2>
    );
}

export default ProductReport;
